import AnimatedPopup from "../core/AnimatedPopup";
import Strings from "../core/data/Strings";
import { HiddenObjectGameData } from "../game/HiddenObjectGame";
import Medal from "../game/Medal";
import { SnowParticles } from "../game/SnowParticles";
import GameColors from "../GameColors";
import PlayerData from "../PlayerData";
import GameScene from "../scenes/GameScene";
import ARIA from "../utils/ARIA";
import Fonts from "../utils/Fonts";
import TimeFormat from "../utils/TimeFormat";

export class RecapPopup extends AnimatedPopup {
	_onResize: () => void;
	scene: GameScene;
	text: Phaser.GameObjects.Text;

	constructor(scene: GameScene, data: HiddenObjectGameData, completionTime: number) {
		super(scene, 0, 0);
		this.scene = scene;
		this.scene.soundManager.play("trainingCompleteSFX");
		this.setDepth(999);

		const saveData = PlayerData.Instance.saveData;
		const overlay = (this._overlay = scene.add.sprite(0, 0, "popupOverlay"));
		overlay.setScale(Math.max(scene.viewport.width / overlay.width, scene.viewport.height / overlay.height));

		overlay.setInteractive();
		this.add(overlay);

		SnowParticles.create(scene, this);

		this._popupGroup = scene.add.container(0, 0);
		this.add(this._popupGroup);

		const popupBG = scene.add.nineSlicedSprite(0, 0, "popupBacker", 1250, 800, 50, 50, 50, 50, null, false);
		this._popupGroup.add(popupBG);

		const popupHeader = scene.add.image(0, 0, "popupHeader");
		this._popupGroup.add(popupHeader);

		const successText = (this.text = scene.add.text(0, 0, Strings.get("GreatSearch"), {
			fontFamily: Fonts.MontserratBold,
			fontSize: "70px",
			color: GameColors.WHITE,
			align: "center",
		}));
		successText.setOrigin(0.5, 0.5);
		this._popupGroup.add(successText);

		const levelTextBG = scene.add.image(0, 0, "popupSmallHeader");
		let title = Strings.get(data.title);
		let dayNightIcon: Phaser.GameObjects.Image;
		if (title.includes("(Day)")) {
			title = title.slice(0, -6);
			dayNightIcon = scene.add.image(0, 0, "recapDay");
		} else {
			title = title.slice(0, -8);
			dayNightIcon = scene.add.image(0, 0, "recapNight");
		}
		const levelText = scene.add.text(0, 0, title, {
			fontFamily: Fonts.MontserratBold,
			fontSize: "50px",
			color: GameColors.PURPLE,
			align: "Center",
		});
		this._popupGroup.add([levelTextBG, dayNightIcon, levelText]);

		// Objects found
		const objsBG = scene.add.image(0, 0, "popupBluePill");
		const objsIcon = scene.add.image(0, 0, "recapObjs");
		const objectsFound = data.objects.toFixed();
		const objsStr = `${objectsFound}/${objectsFound}`;
		const objsText = scene.add.text(0, 0, objsStr, {
			fontFamily: Fonts.MontserratBold,
			fontSize: "50px",
			color: GameColors.WHITE,
			align: "left",
		});
		this._popupGroup.add([objsBG, objsIcon, objsText]);

		// Time
		const timeBG = scene.add.image(0, 0, "popupBluePill");
		const timeIcon = scene.add.image(0, 0, "recapTime");
		const timeStr = "" + TimeFormat.format(completionTime, 0);
		const timeText = scene.add.text(0, 0, timeStr, {
			fontFamily: Fonts.MontserratBold,
			fontSize: "50px",
			color: GameColors.WHITE,
			align: "left",
		});
		this._popupGroup.add([timeBG, timeIcon, timeText]);

		const previousMedal = saveData.medalsEarned[saveData.currentLevel];
		const newMedal = this.calculateMedal(data.objects);
		const scoreBG = scene.add.image(0, 0, "popupScore");

		const score = this.scene.hiddenObjGame.currentLevel.score;
		const scoreText = scene.add.text(0, 0, "" + score, {
			fontFamily: Fonts.MontserratBold,
			fontSize: "70px",
			color: GameColors.WHITE,
			align: "center",
		});
		this._popupGroup.add([scoreBG, scoreText]);

		// Medal and Score
		const rays = this.scene.add.image(0, 0, "objectBarRays");
		const glow = this.scene.add.image(0, 0, "objectBarRaysGlow");
		this._popupGroup.add([rays, glow]);

		this.scene.add.tween({
			targets: [rays, glow],
			alpha: { from: 0, to: 0.8 },
			scale: { from: 0, to: 1 },
			duration: 500,
			loopDelay: 500,
			yoyo: true,
			ease: Phaser.Math.Easing.Quadratic.Out,
		});

		this.scene.add.tween({
			targets: rays,
			angle: 90,
			duration: 1500,
		});

		const medal = scene.add.image(0, 0, "popupMedal" + newMedal);
		const newBestText = scene.add.image(0, 0, "popupNewBest").setAngle(25);
		this._popupGroup.add([medal, newBestText]);

		const flurry = scene.add.image(0, 0, "recapFlurry");
		this._popupGroup.add(flurry);

		if (Medal.convertToNumber(previousMedal) < Medal.convertToNumber(newMedal)) {
			if (saveData.medalsEarned[saveData.currentLevel] == Medal.None) {
				saveData.lastLevelMedal = saveData.currentLevel;
			}
			saveData.medalsEarned[saveData.currentLevel] = newMedal;
			// refresh certification variable based on new best to show it again on level select
			if (saveData.certificationEarned) saveData.certificationEarned = false;
			PlayerData.Instance.save();
			// show new best
			newBestText.setVisible(true);
		} else {
			newBestText.setVisible(false);
		}

		const replayBtn = scene.add.button(0, 0, "replayBtn", null, () => this.replay(), this, true);
		this._popupGroup.add(replayBtn);

		const homeBtn = scene.add.button(0, 0, "nextBtn", null, () => this.close(), this, true);
		this._popupGroup.add(homeBtn);

		replayBtn.setSize(homeBtn.width, homeBtn.height);
		replayBtn.setDisplaySize(homeBtn.width, homeBtn.height);

		const scoreDifference = data.objects * 85 - score;
		const goldText = this.scene.add.text(0, 0, Strings.get("RecapGold").replace("%N", scoreDifference.toString()), {
			fontFamily: Fonts.MontserratExtraBold,
			fontSize: "35px",
			color: GameColors.PURPLE,
			align: "center",
		});
		this._popupGroup.add(goldText);
		if (scoreDifference < 0) goldText.setVisible(false);

		this._onResize = (): void => {
			scene.alignIn.Center(overlay, scene.viewport);
			scene.alignIn.Center(this._popupGroup, overlay, 0, -15);
			scene.alignTo.TopCenter(popupHeader, popupBG, 0, -70);
			scene.alignIn.Center(successText, popupHeader, 0);

			scene.alignIn.Center(levelTextBG, popupBG, 0, -250);
			scene.alignIn.LeftCenter(dayNightIcon, levelTextBG);
			scene.alignIn.Center(levelText, levelTextBG);

			scene.alignIn.Center(objsBG, popupBG, 230, -125);
			scene.alignIn.LeftCenter(objsIcon, objsBG, 10);
			scene.alignIn.Center(objsText, objsBG);

			scene.alignIn.Center(timeBG, popupBG, -230, -125);
			scene.alignIn.LeftCenter(timeIcon, timeBG, 10);
			scene.alignIn.Center(timeText, timeBG);

			scene.alignIn.Center(scoreBG, popupBG, 0, 50);
			scene.alignIn.Center(scoreText, scoreBG);

			scene.alignTo.BottomCenter(goldText, scoreBG, 0, 25);
			scene.alignTo.BottomCenter(replayBtn, popupBG, -150, -replayBtn.height * 0.75);
			scene.alignTo.BottomCenter(homeBtn, popupBG, 150, -homeBtn.height * 0.75);

			scene.alignTo.RightCenter(medal, scoreBG, -250);
			scene.alignIn.Center(rays, medal, 90, 90);
			scene.alignIn.Center(glow, medal, 90, 90);
			scene.alignTo.TopRight(newBestText, medal, 70, -100);

			scene.alignIn.BottomLeft(flurry, popupBG, flurry.width * 0.65, flurry.height * 0.3);

			const screenRatio = this.scene.viewport.width / this.scene.viewport.height;
			const goalRatio = 16 / 9;
			const ratioDif = screenRatio - goalRatio;
			if (screenRatio < goalRatio) {
				this._popupGroup.scale = 1 - ratioDif * ratioDif;
			}
		};
		this.resize();

		const ariaLayer = "recap-popup";
		successText.setARIA(ariaLayer, true);
		levelText.setARIA(ariaLayer);
		const minutes = Math.floor(completionTime / 60);
		const seconds = Math.floor(completionTime % 60);
		let time = `${seconds.toFixed()} seconds`;
		if (minutes > 0) time = `${minutes.toFixed()} minutes and ${time}`;
		ARIA.addTextWithObject(timeBG, `Final Time: ${time}`, ariaLayer);
		ARIA.addTextWithObject(objsBG, `Found ${objectsFound} out of ${objectsFound} Objects`, ariaLayer);
		ARIA.addTextWithObject(scoreBG, `Final Score: ${score.toFixed()}`, ariaLayer);
		if (goldText.visible) goldText.setARIA(ariaLayer);
		replayBtn.setARIA("Replay", ariaLayer);
		homeBtn.setARIA("Continue", ariaLayer);
		ARIA.showLayer(ariaLayer, true);

		this.intro();
	}

	calculateMedal(numOfItems: number): string {
		const score = this.scene.hiddenObjGame.currentLevel.score;
		if (score >= numOfItems * 85) return Medal.GOLD;
		else if (score >= numOfItems * 65) return Medal.SILVER;
		else return Medal.BRONZE;
	}

	replay(): void {
		this.scene.scene.start("PreloaderScene", { destinationScene: "GameScene" });
		this.scene.scene.stop(this.scene.scene.key);
	}

	close(): void {
		this.scene.scene.start("PreloaderScene", { destinationScene: "LevelSelectScene" });
		this.scene.scene.stop(this.scene.scene.key);
	}

	resize(): void {
		super.resize();
		if (this._onResize != null) this._onResize();
	}
}
